/**
 * Changes by person (collaboration design, board "Changes by person"): the project's changes grouped by who made them
 * (each person, and each AI agent with the person who drove it), by agent run or by file, with a diff per change.
 * "Revert mine…", "Revert this run…" or "Revert X's changes…" (only where vfs-service says the caller may) first asks
 * vfs-service for a dry run: when others changed the same lines later, the revert is refused and the overlapping lines
 * are listed with who changed them; otherwise one confirmation, and the revert lands as a new commit by the caller.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import Modal, { ModalFrame } from '../Modal';
import ToastContainer from '../ToastContainer';
import { useToast } from '../../hooks/useToast';
import vfsClient from '../../utils/vfsClient';
import { getChanges, revertChanges } from '../../services/CollabService';
import {
  authorChips, diffLinesFor, filterChanges, groupChanges, plural, repoPathOf, revertLabel, sinceFor, timeRange, whoLabel,
} from '../../utils/changesModel';
import { ParticipantMark } from '../presence/Presence';
import ToolIcon, { vendorOfTool } from '../sandbox/ToolIcon';
import './Collab.css';
import { CollabFilters } from './BottomSheet';
import HistoryEmpty from './HistoryEmpty';

const agentSlot = (via) => (via === 'Flow' ? 3 : 4);

/**
 * The mark of an author or an agent (square, the driver's initials). An agent of a known vendor carries its logo, small,
 * on the bottom-right corner, so it does not read as a person.
 */
export function AuthorMark({ author, via, size = 24, you }) {
  const label = whoLabel({ author, via }, you);
  if (!via) { return <ParticipantMark initials={author?.initials || '?'} label={label} size={size} slot={author?.slot ?? 0} />; }
  const mark = <ParticipantMark agent initials={author?.initials || 'AI'} label={label} size={size} slot={agentSlot(via)} />;
  const vendor = vendorOfTool(via);
  if (!vendor) { return mark; }
  const badge = Math.max(10, Math.round(size * 0.55));
  return (
    <span className="collab-author-mark" data-vendor={vendor.label}>
      {mark}
      <span className="collab-author-vendor" style={{ width: badge, height: badge }}>
        <ToolIcon icon={vendor.icon} label={vendor.label} size={badge - 4} />
      </span>
    </span>
  );
}

AuthorMark.propTypes = {
  author: PropTypes.object,
  size: PropTypes.number,
  via: PropTypes.string,
  you: PropTypes.object,
};

/** One file's lines of one commit, as a small diff. */
function FileDiff({ projectId, sha, path, cache, onLoaded }) {
  const text = cache[sha];
  useEffect(() => {
    if (text !== undefined) { return; }
    let cancelled = false;
    vfsClient.getCommitDiff(projectId, sha)
      .then((diff) => { if (!cancelled) { onLoaded(sha, diff); } })
      .catch(() => { if (!cancelled) { onLoaded(sha, null); } });
    return () => { cancelled = true; };
  }, [projectId, sha, text, onLoaded]);
  if (text === undefined) { return <div className="collab-muted collab-pad">Loading diff…</div>; }
  if (text === null) { return <div className="collab-muted collab-pad">The diff could not be loaded.</div>; }
  const lines = diffLinesFor(text, repoPathOf(path));
  if (lines.length === 0) { return <div className="collab-muted collab-pad">No text changes (binary or renamed).</div>; }
  return (
    <div className="collab-diff" data-testid="collab-diff">
      {lines.slice(0, 400).map((line, index) => (
        // eslint-disable-next-line react/no-array-index-key
        <div className={`collab-diff-line is-${line.kind}`} key={index}>
          <span className="collab-diff-n">{line.kind === 'del' ? line.oldLine : line.kind === 'hunk' ? '' : line.newLine}</span>
          <span className="collab-diff-m">{line.kind === 'add' ? '+' : line.kind === 'del' ? '−' : ''}</span>
          <span className="collab-diff-t">{line.text}</span>
        </div>
      ))}
    </div>
  );
}

FileDiff.propTypes = {
  cache: PropTypes.object.isRequired,
  onLoaded: PropTypes.func.isRequired,
  path: PropTypes.string.isRequired,
  projectId: PropTypes.string.isRequired,
  sha: PropTypes.string.isRequired,
};

const shortTime = (iso) => (iso ? new Date(iso).toLocaleString([], { weekday: 'short', hour: '2-digit', minute: '2-digit' }) : '');

function GroupCard({ group, you, projectId, cache, onLoaded, onRevert }) {
  const [openPath, setOpenPath] = useState(null);
  const label = revertLabel(group, you);
  const title = group.kind === 'file'
    ? <span className="collab-mono">{group.path}</span>
    : <strong>{whoLabel(group, you)}</strong>;
  return (
    <section className="collab-group" data-testid="changes-group">
      <header className="collab-group-head">
        {group.kind === 'file'
          ? (group.who || []).slice(0, 4).map((w) => <AuthorMark author={w.author} key={`${w.author?.id}|${w.via}`} size={22} via={w.via} you={you} />)
          : <AuthorMark author={group.author} size={28} via={group.via} you={you} />}
        <div className="collab-group-title">
          <div>
            {title}
            {group.kind !== 'file' && <span className="collab-muted"> · {plural(group.changes.length, 'change')} in {plural(group.files.length, 'file')}</span>}
          </div>
          {group.description && <div className="collab-group-desc" data-testid="changes-run-description">{group.description}</div>}
          <div className="collab-muted collab-small">
            {timeRange(group.from, group.to)} · <span className="collab-add">+{group.added}</span> <span className="collab-del">−{group.removed}</span> lines
            {group.via && group.aiModel ? ` · ${group.aiModel}` : ''}
          </div>
        </div>
        {label && (
          <button className="collab-btn" onClick={() => onRevert(group, label)} type="button">{label}</button>
        )}
      </header>
      {group.kind === 'file' ? (
        group.changes.map((change) => (
          <div key={change.sha}>
            <button className="collab-file-row" onClick={() => setOpenPath(openPath === change.sha ? null : change.sha)} type="button">
              <AuthorMark author={change.author} size={18} via={change.via} you={you} />
              <span>{whoLabel(change, you)}</span>
              <span className="collab-muted collab-small">{shortTime(change.timestamp)} · {change.message}</span>
            </button>
            {openPath === change.sha && <FileDiff cache={cache} onLoaded={onLoaded} path={group.path} projectId={projectId} sha={change.sha} />}
          </div>
        ))
      ) : (
        group.files.map((file) => (
          <div key={file.path}>
            <button className="collab-file-row" onClick={() => setOpenPath(openPath === file.path ? null : file.path)} type="button">
              <span className="collab-mono">{file.path}</span>
              <span className="collab-small">
                {file.created ? 'new · ' : ''}{file.deleted ? 'deleted · ' : ''}<span className="collab-add">+{file.added}</span> <span className="collab-del">−{file.removed}</span>
              </span>
            </button>
            {openPath === file.path && group.changes
              .filter((change) => (change.files || []).some((f) => f.path === file.path))
              .map((change) => (
                <div key={change.sha}>
                  <div className="collab-muted collab-small collab-pad">{shortTime(change.timestamp)} · {change.message}</div>
                  <FileDiff cache={cache} onLoaded={onLoaded} path={file.path} projectId={projectId} sha={change.sha} />
                </div>
              ))}
          </div>
        ))
      )}
    </section>
  );
}

GroupCard.propTypes = {
  cache: PropTypes.object.isRequired,
  group: PropTypes.object.isRequired,
  onLoaded: PropTypes.func.isRequired,
  onRevert: PropTypes.func.isRequired,
  projectId: PropTypes.string.isRequired,
  you: PropTypes.object,
};

/** The revert dialog: checking (dry run), the conflicts that refuse it, or the confirmation. */
export function RevertDialog({ state, you, onCancel, onConfirm }) {
  if (!state) { return null; }
  const { group, label, phase, result, error } = state;
  let body;
  if (phase === 'checking') {
    body = <p>Checking whether others built on these changes…</p>;
  } else if (phase === 'conflicts') {
    body = (
      <div data-testid="revert-conflicts">
        <p><strong>Not reverted.</strong> Later changes by others touched the same lines, and Flow never drops someone else’s work. Ask them, or change these lines by hand:</p>
        <ul className="collab-conflicts">
          {(error?.conflicts || []).map((c, index) => (
            // eslint-disable-next-line react/no-array-index-key
            <li key={index}>
              <span className="collab-mono">{c.path}</span>
              {c.lines ? ` lines ${c.lines.start}${c.lines.end !== c.lines.start ? `–${c.lines.end}` : ''}` : ''}
              {' · changed by '}<strong>{whoLabel({ author: c.by, via: c.via }, you)}</strong>
              {c.timestamp ? <span className="collab-muted"> · {shortTime(c.timestamp)}</span> : null}
            </li>
          ))}
        </ul>
      </div>
    );
  } else if (phase === 'error') {
    body = <p className="collab-error">{error?.message || 'The revert could not be checked.'}</p>;
  } else {
    const files = result?.files || group.files;
    body = (
      <div>
        <p>
          Revert only {group.kind === 'run' ? 'this run’s' : `${whoLabel(group, you)}’s`} {plural(group.changes.length, 'change')} in {plural(files.length, 'file')}.
          Nobody else’s later work is touched; the revert is a new change by you, so it can be undone from History.
        </p>
        <ul className="collab-conflicts">
          {files.slice(0, 12).map((f) => <li className="collab-mono" key={f.path}>{f.path}{f.action ? ` · ${f.action}` : ''}</li>)}
        </ul>
      </div>
    );
  }
  const ready = phase === 'ready' || phase === 'reverting';
  return (
    <Modal className="collab-revert-modal" isOpen onClose={phase === 'reverting' ? null : onCancel} title={label.replace(/…$/, '')} type="custom">
      <div className="collab-revert-body">{body}</div>
      <div className="modal-footer">
        <button className="modal-btn modal-btn-cancel" disabled={phase === 'reverting'} onClick={onCancel} type="button">
          {ready ? 'Cancel' : 'Close'}
        </button>
        {ready && (
          <button className="modal-btn modal-btn-confirm dangerous" data-testid="revert-confirm" disabled={phase === 'reverting'} onClick={onConfirm} type="button">
            {phase === 'reverting' ? 'Reverting…' : `Revert ${plural(group.changes.length, 'change')}`}
          </button>
        )}
      </div>
    </Modal>
  );
}

RevertDialog.propTypes = {
  onCancel: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  state: PropTypes.object,
  you: PropTypes.object,
};

const GROUP_BY = [
  { value: 'person', label: 'Person and agent' },
  { value: 'run', label: 'Agent run' },
  { value: 'file', label: 'File' },
];
const RANGES = [
  { value: 'today', label: 'Today' },
  { value: '7d', label: '7 days' },
  { value: 'all', label: 'All' },
];

const ChangesPanel = ({ isOpen, onClose, projectId, focus = null, embedded = false }) => {
  const { toasts, showToast, removeToast } = useToast();
  const [range, setRange] = useState('7d');
  const [by, setBy] = useState('person');
  const [picked, setPicked] = useState([]);
  const [onlyMine, setOnlyMine] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [cache, setCache] = useState({});
  const [revert, setRevert] = useState(null);

  const load = useCallback(async () => {
    if (!projectId) { return; }
    setLoading(true);
    setLoadError(null);
    try {
      setData(await getChanges(projectId, { since: sinceFor(range), limit: 500 }));
    } catch (error) {
      setLoadError(error.message || 'Changes could not be loaded');
    } finally {
      setLoading(false);
    }
  }, [projectId, range]);

  useEffect(() => {
    if (isOpen) { load(); }
  }, [isOpen, load]);

  const onLoaded = useCallback((sha, text) => setCache((current) => ({ ...current, [sha]: text })), []);
  const you = data?.you || null;
  const chips = useMemo(() => authorChips(data), [data]);
  // Opened from the Activity timeline on some changes (`focus.shas`): only those, until "Show all".
  const [focused, setFocused] = useState(null);
  useEffect(() => {
    if (isOpen && focus?.shas?.length) {
      setFocused(focus.shas);
      setRange('all');
      if (focus.runId) { setBy('run'); }
    } else if (!isOpen) {
      setFocused(null);
    }
  }, [isOpen, focus]);
  const groups = useMemo(() => {
    const grouped = groupChanges(filterChanges((data?.changes || []).filter((c) => !focused || focused.includes(c.sha)), { authors: picked, onlyMine, you }), by);
    // An agent run says what it did (vfs-service's `runs[].description`: its commits' own words, else its files).
    const runs = new Map((data?.runs || []).map((run) => [run.id, run]));
    return grouped.map((group) => (group.kind === 'run' && runs.get(group.runId)?.description ? { ...group, description: runs.get(group.runId).description } : group));
  }, [data, picked, onlyMine, you, by, focused]);

  const startRevert = async (group, label) => {
    setRevert({ group, label, phase: 'checking' });
    try {
      const result = await revertChanges(projectId, group.shas, { dryRun: true });
      setRevert({ group, label, phase: 'ready', result });
    } catch (error) {
      if (error.status === 409) {
        setRevert({ group, label, phase: 'conflicts', error: error.body });
      } else {
        setRevert({ group, label, phase: 'error', error: { message: error.message } });
      }
    }
  };

  const confirmRevert = async () => {
    if (!revert) { return; }
    const { group, label } = revert;
    setRevert({ ...revert, phase: 'reverting' });
    try {
      const result = await revertChanges(projectId, group.shas);
      setRevert(null);
      showToast(`Reverted: ${plural((result?.files || []).length, 'file')} changed in a new change by you.`, 'success');
      load();
    } catch (error) {
      if (error.status === 409) {
        setRevert({ group, label, phase: 'conflicts', error: error.body });
      } else {
        setRevert({ group, label, phase: 'error', error: { message: error.message } });
      }
    }
  };

  if (!isOpen) { return null; }

  const toggle = (key) => setPicked((current) => (current.includes(key) ? current.filter((k) => k !== key) : [...current, key]));
  const filtering = Boolean(picked.length || onlyMine || focused);
  const clearFilters = () => { setPicked([]); setOnlyMine(false); setFocused(null); };

  return (
    <>
      <ModalFrame className="collab-changes-modal" embedded={embedded} isOpen={isOpen} onClose={onClose} title="Changes by person" type="custom">
        <div className="collab-changes">
          <CollabFilters summary={[RANGES.find((r) => r.value === range)?.label, picked.length ? plural(picked.length, 'person') : 'Everyone', `by ${GROUP_BY.find((g) => g.value === by)?.label.toLowerCase()}`].join(' · ')} active={picked.length + (onlyMine ? 1 : 0)}>
            <div className="collab-filter-title">Since</div>
            <div className="collab-seg">
              {RANGES.map((r) => (
                <button aria-pressed={range === r.value} className={range === r.value ? 'is-on' : ''} key={r.value} onClick={() => setRange(r.value)} type="button">{r.label}</button>
              ))}
            </div>
            <div className="collab-filter-title">Who</div>
            {chips.length === 0 && <div className="collab-muted collab-small">Nobody yet.</div>}
            {chips.map((chip) => (
              <button
                aria-pressed={picked.includes(chip.key)}
                className={`collab-chip${picked.includes(chip.key) ? ' is-on' : ''}`}
                data-testid="author-chip"
                key={chip.key}
                onClick={() => toggle(chip.key)}
                type="button"
              >
                <AuthorMark author={chip.author} size={20} via={chip.via} you={you} />
                <span className="collab-chip-name">{whoLabel(chip, you)}</span>
                <span className="collab-chip-count">{chip.count}</span>
              </button>
            ))}
            <div className="collab-filter-title">Group by</div>
            {GROUP_BY.map((g) => (
              <label className="collab-radio" key={g.value}>
                <input checked={by === g.value} name="collab-group-by" onChange={() => setBy(g.value)} type="radio" />
                {g.label}
              </label>
            ))}
            <label className="collab-radio">
              <input checked={onlyMine} onChange={(e) => setOnlyMine(e.target.checked)} type="checkbox" />
              Only files I also touched
            </label>
            <p className="collab-muted collab-small">
              Every write is a change with an author. An AI agent’s changes are credited to the person who drove it, with the tool and model.
            </p>
          </CollabFilters>
          <div className="collab-groups">
            {focused && (
              <div className="collab-small" data-testid="changes-focus">
                Showing {plural(focused.length, 'change')} from the activity · <button className="collab-link" onClick={() => setFocused(null)} type="button">Show all</button>
              </div>
            )}
            {loading && <div className="collab-muted">Loading changes…</div>}
            {loadError && <div className="collab-error">{loadError}</div>}
            {!loading && !loadError && groups.length === 0 && (
              <HistoryEmpty onClear={filtering ? clearFilters : null} testId="changes-empty">
                {filtering ? 'No changes in this period match the filters.' : 'No changes in this period.'}
              </HistoryEmpty>
            )}
            {!loading && !loadError && groups.map((group) => (
              <GroupCard cache={cache} group={group} key={group.key} onLoaded={onLoaded} onRevert={startRevert} projectId={projectId} you={you} />
            ))}
          </div>
        </div>
      </ModalFrame>
      <RevertDialog onCancel={() => (revert?.phase === 'reverting' ? null : setRevert(null))} onConfirm={confirmRevert} state={revert} you={you} />
      <ToastContainer onRemoveToast={removeToast} toasts={toasts} />
    </>
  );
};

ChangesPanel.propTypes = {
  embedded: PropTypes.bool,
  focus: PropTypes.object,
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  projectId: PropTypes.string,
};

export default ChangesPanel;
